import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EmailIcon from "@mui/icons-material/Email";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import { Box, Button, Card, CircularProgress, Divider, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { UI } from "./constants";

export const EmployeeInviteCard = ({ workforce, companyName, employeeFullName, sendingInvitation, emailSent, result, handleSendDownloadEmail }) => {
  const [email, setEmail] = useState(workforce?.email || "");
  const [emailError, setEmailError] = useState("");

  const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    if (newEmail && !validateEmail(newEmail)) setEmailError("Please enter a valid email address");
    else setEmailError("");
  };

  const handleSendEmail = () => {
    if (!email) return setEmailError("Email is required");
    if (!validateEmail(email)) return setEmailError("Please enter a valid email address");
    handleSendDownloadEmail(email, companyName, employeeFullName);
  };

  return (
    <Card
      sx={{
        borderRadius: UI.cardRadius,
        border: "none",
        boxShadow: "none",
        p: { xs: "1.25rem", md: "2rem" },
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack spacing="0.5rem" sx={{ mb: { xs: "1rem", md: "2rem" } }}>
        <Stack direction="row" spacing="0.75rem" alignItems="center">
          <PersonAddAltIcon sx={{ fontSize: "2.5rem", color: UI.primary }} />
          <Typography
            sx={{
              fontSize: { xs: "1.5rem", md: "1.75rem" },
              fontWeight: 800,
              letterSpacing: "-0.02em",
            }}
          >
            Invite Employee
          </Typography>
        </Stack>
        <Typography sx={{ color: UI.textMuted, fontSize: { xs: "0.875rem", md: "1rem" } }}>
          Send a direct invitation link to a team member&apos;s email.
        </Typography>
      </Stack>

      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: "1rem" }}>
        <Box>
          <TextField
            fullWidth
            value={email}
            onChange={handleEmailChange}
            error={!!emailError}
            helperText={emailError || "We'll send the Flownect app download link here"}
            disabled={sendingInvitation}
            placeholder="employee@company.com"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MailOutlineIcon sx={{ color: "rgba(100, 116, 139, 0.9)", fontSize: "1.25rem" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 1)",
                borderRadius: "0.75rem",
                fontSize: "0.875rem",
                py: "0.125rem",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(15, 23, 42, 0.10)",
              },
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: UI.primary,
                borderWidth: "0.125rem",
              },
              "& .MuiFormHelperText-root": {
                mt: "0.25rem",
                fontSize: "0.7rem",
              },
            }}
          />

          <Button
            fullWidth
            onClick={handleSendEmail}
            disabled={sendingInvitation || !!emailError || !email}
            variant="outlined"
            startIcon={
              sendingInvitation ? (
                <CircularProgress size={16} />
              ) : emailSent ? (
                <CheckCircleIcon sx={{ fontSize: "1.25rem" }} />
              ) : (
                <EmailIcon sx={{ fontSize: "1.25rem" }} />
              )
            }
            sx={{
              mt: "0.75rem",
              borderWidth: "0.125rem",
              borderColor: emailSent ? "#16a34a" : UI.primary,
              color: emailSent ? "#16a34a" : UI.primary,
              "&:hover": {
                borderWidth: "0.125rem",
                borderColor: emailSent ? "#16a34a" : UI.primary,
                bgcolor: "rgba(236, 127, 19, 0.05)",
              },
              borderRadius: "0.75rem",
              py: "0.75rem",
              fontWeight: 900,
              fontSize: "1rem",
              textTransform: "none",
            }}
          >
            {sendingInvitation ? "Sending..." : emailSent ? "Email sent!" : "Send Invitation"}
          </Button>

          {(result?.success || result?.error) && (
            <Typography
              sx={{
                mt: "0.5rem",
                fontSize: "0.7rem",
                textAlign: "center",
                color: result?.success ? "#16a34a" : "#ef4444",
              }}
            >
              {result?.success || result?.error}
            </Typography>
          )}
        </Box>
      </Box>
    </Card>
  );
};
